import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { Municipality, UseOfForceReportHeaderModel, UseOfForceSupervisor, UseOfForceUnitOverviewItemModel } from '../../api-service/clients';
import { formatDateTime } from '../../utilities';
import { hygieneRestraintLogText, interventionTypeShortLabels, supervisorLabels } from '../citizen/serviceprovider/use-of-force/use-of-force-labels';
import { getMunicipalities } from './use-of-force-report-api';
import { groupByInterventionMonth } from './use-of-force-report-months';

interface UseOfForceReportDialogProps {
  title: string;
  header: UseOfForceReportHeaderModel;
  items: UseOfForceUnitOverviewItemModel[];
  selectedIncidentIds: string[];
  onSave: (header: UseOfForceReportHeaderModel, incidentIds: string[]) => Promise<void>;
  onClose: () => void;
}

export function UseOfForceReportDialog(props: UseOfForceReportDialogProps) {
  const { title, items, onSave, onClose } = props;
  const [header, setHeader] = useState(props.header);
  const [selectedIds, setSelectedIds] = useState(props.selectedIncidentIds);
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let isCurrent = true;
    async function load() {
      const result = await getMunicipalities();
      if (isCurrent) setMunicipalities(result);
    }
    load();
    return () => {
      isCurrent = false;
    };
  }, []);

  function change(changes: Partial<UseOfForceReportHeaderModel>) {
    setHeader(current => ({ ...current, ...changes }));
  }

  function toggle(incidentId: string) {
    setSelectedIds(current => (current.includes(incidentId) ? current.filter(id => id !== incidentId) : [...current, incidentId]));
  }

  function toggleMonth(monthIds: string[], select: boolean) {
    setSelectedIds(current => (select ? [...new Set([...current, ...monthIds])] : current.filter(id => !monthIds.includes(id))));
  }

  async function save() {
    setIsSaving(true);
    try {
      await onSave(header, selectedIds);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onClose={() => (isSaving ? undefined : onClose())} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField select label="Tilsyn" value={header.supervisor ?? ''} onChange={event => change({ supervisor: event.target.value as UseOfForceSupervisor })}>
            {Object.values(UseOfForceSupervisor).map(supervisor => (
              <MenuItem key={supervisor} value={supervisor}>
                {supervisorLabels[supervisor]}
              </MenuItem>
            ))}
          </TextField>
          {header.supervisor === UseOfForceSupervisor.Municipality && (
            <TextField
              select
              label="Kommunen, der fører tilsynet"
              value={header.supervisionMunicipalityId ?? ''}
              onChange={event => change({ supervisionMunicipalityId: event.target.value })}
            >
              {municipalities.map(municipality => (
                <MenuItem key={municipality.id} value={municipality.id}>
                  {municipality.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Typography variant="body2">Driftsherren orienteres kun, når tilbuddet er kommunalt eller regionalt.</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Driftsherrens navn" value={header.operatorName ?? ''} onChange={event => change({ operatorName: event.target.value })} sx={{ flex: 2 }} />
            <TextField
              label="Driftsherrens CVR-nummer"
              value={header.operatorCvr ?? ''}
              onChange={event => change({ operatorCvr: event.target.value })}
              slotProps={{ htmlInput: { maxLength: 8, inputMode: 'numeric' } }}
              sx={{ flex: 1 }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2">Skemaer i indberetningen</Typography>
            <Typography variant="body2" color="text.secondary">
              Viser godkendte skemaer, der endnu ikke er indberettet til tilsynet.
            </Typography>
          </Box>
          {groupByInterventionMonth(items).map(month => {
            const monthIds = month.items.map(item => item.incident.id);
            const selectedInMonth = monthIds.filter(id => selectedIds.includes(id)).length;
            return (
              <Box key={month.key} sx={{ display: 'flex', flexDirection: 'column' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedInMonth === monthIds.length}
                      indeterminate={selectedInMonth > 0 && selectedInMonth < monthIds.length}
                      onChange={() => toggleMonth(monthIds, selectedInMonth < monthIds.length)}
                    />
                  }
                  label={<Typography variant="subtitle2">{`${month.label} (${selectedInMonth} af ${monthIds.length})`}</Typography>}
                />
                {month.items.map(item => (
                  <FormControlLabel
                    key={item.incident.id}
                    sx={{ ml: 3 }}
                    control={<Checkbox checked={selectedIds.includes(item.incident.id)} onChange={() => toggle(item.incident.id)} />}
                    label={[
                      item.incident.draft.interventionStartedAt ? formatDateTime(item.incident.draft.interventionStartedAt) : undefined,
                      item.citizenName ?? '-',
                      item.incident.draft.interventionType ? interventionTypeShortLabels[item.incident.draft.interventionType] : undefined,
                      item.hygieneRestraintLogStatus ? hygieneRestraintLogText(item.hygieneRestraintLogStatus) : undefined,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isSaving}>
          Afbryd
        </Button>
        <Button variant="contained" onClick={save} disabled={isSaving || selectedIds.length === 0}>
          Gem
        </Button>
      </DialogActions>
    </Dialog>
  );
}
